import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { Message } from "@/entities/message";
import { formatDateForSeparator } from "./format-date-for-separator";

export interface UseFloatingDateProps {
  messages: Message[] | undefined;
  messageRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  chatContainerRef: RefObject<HTMLDivElement | null>;
  enabled?: boolean;
}

export const useFloatingDate = ({
  messages,
  messageRefs,
  chatContainerRef,
  enabled = true,
}: UseFloatingDateProps) => {
  const [floatingDate, setFloatingDate] = useState<Date | null>(null);
  const [isFading, setIsFading] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateFloatingDate = useCallback(() => {
    if (!enabled) return;

    const container = chatContainerRef.current;
    if (!container || !messages?.length) return;

    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;
    const scrollBottom = scrollTop + clientHeight;

    // Найти первое (верхнее) видимое сообщение
    let firstVisibleMsgIndex = -1;
    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      const el = messageRefs.current[msg._id];
      if (!el) continue;

      const elTop = el.offsetTop;
      const elBottom = elTop + el.offsetHeight;

      if (elBottom > scrollTop && elTop < scrollBottom) {
        firstVisibleMsgIndex = i;
        break;
      }
    }

    if (firstVisibleMsgIndex === -1) {
      setIsFading(true);
      setTimeout(() => {
        setFloatingDate(null);
        setIsFading(false);
      }, 300);
      return;
    }

    // Найти ближайшую дату-разделитель выше или у этого сообщения
    let newDate: Date | null = null;
    for (let i = firstVisibleMsgIndex; i >= 0; i--) {
      const msg = messages[i];
      const msgDate = new Date(msg.createdAt || msg.createdAt);

      const shouldShowDate =
        i === 0 ||
        Math.abs(
          new Date(
            messages[i - 1].createdAt || messages[i - 1].createdAt
          ).getTime() - msgDate.getTime()
        ) >
          24 * 60 * 60 * 1000;

      if (shouldShowDate) {
        newDate = msgDate;
        break;
      }
    }

    // Обновить дату, если изменилась
    if (floatingDate?.getTime() !== newDate?.getTime()) {
      setFloatingDate(newDate);
      setIsFading(false);
    }

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Запланировать fade-out
    scrollTimeoutRef.current = setTimeout(() => {
      setIsFading(true);
      setTimeout(() => {
        setFloatingDate(null);
        setIsFading(false);
      }, 300);
    }, 300);
  }, [messages, messageRefs, chatContainerRef, enabled, floatingDate]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return {
    floatingDate,
    isFading,
    updateFloatingDate,
    formattedFloatingDate: floatingDate
      ? formatDateForSeparator(floatingDate)
      : null,
  };
};
